import React from "react";
import styled, { css } from "styled-components";
import { colors } from "./colors";
import { rgba } from "polished";
import FeatherIcon from "feather-icons-react";
import { TooltipOverlay } from "../TooltipOverlay";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "_metronic/_helpers";
import MaterialIcon from "./MaterialIcon";
import Spinner from "./Spinner";

const getIconColor = (variant, buttonStyle, isHover = false, iconVariant) => {
    let effectiveVariant = isHover && iconVariant ? iconVariant : variant;
    if (!colors[effectiveVariant]) effectiveVariant = "primary";
    switch (buttonStyle) {
        case "inverted":
        case "option":
            return colors[effectiveVariant][900];
        case "borderless":
            return colors[effectiveVariant][900];
        case "tag":
            return colors[effectiveVariant][700];
        case "selectedOption":
            return effectiveVariant === "neutral" ? colors.neutral[700] : colors[effectiveVariant][900];
        case "default":
        default:
            return colors[effectiveVariant][50];
    }
};

const StyledMaterialIcon = styled(MaterialIcon)`
    margin: ${({ position }) =>
        position === "right" ? "0 0 0 8px" : position === "left" ? "0 8px 0 0" : position === "center" && "0"};
    cursor: ${({ onClick }) => (onClick ? "pointer" : "inherit")};
    color: ${({ variant, $buttonStyle }) => getIconColor(variant, $buttonStyle)};
    transition: color 0.2s ease;
    font-size: 16px;

    &:hover {
        color: ${({ variant, $buttonStyle, $iconVariant }) => getIconColor(variant, $buttonStyle, true, $iconVariant)};
    }
`;

const StyledFeatherIcon = styled(FeatherIcon)`
    margin: ${({ position }) =>
        position === "right" ? "0 0 0 8px" : position === "left" ? "0 8px 0 0" : position === "center" && "0"};
    cursor: ${({ onClick }) => (onClick ? "pointer" : "inherit")};
    color: ${({ variant, $buttonStyle }) => getIconColor(variant, $buttonStyle)};
    transition: color 0.2s ease;

    &:hover {
        color: ${({ variant, $buttonStyle, $iconVariant }) => getIconColor(variant, $buttonStyle, true, $iconVariant)};
    }
`;

/**
 * 16px to match the other two icon mechanisms in this file — `StyledFeatherIcon` passes
 * `size="16"` and `StyledMaterialIcon` sets `font-size: 16px`. This one was 12px, so an SVG
 * icon came out visibly smaller than a feather icon in the same button for no stated reason.
 * The legacy button draws these at 24px because it constrains nothing and the SVG's own
 * dimensions win; 16px is the kit's size, not that one.
 */
const StyledSVG = styled(SVG)`
    margin: ${({ position }) =>
        position === "right" ? "0 0 0 8px" : position === "left" ? "0 8px 0 0" : position === "center" && "0"};
    cursor: ${({ onClick }) => (onClick ? "pointer" : "inherit")};
    width: 16px;
    height: 16px;
    transition: color 0.2s ease;

    svg {
        fill: ${({ variant, $buttonStyle }) => getIconColor(variant, $buttonStyle)};
        transition: fill 0.2s ease;

        &:hover {
            fill: ${({ variant, $buttonStyle, $iconVariant }) =>
                getIconColor(variant, $buttonStyle, true, $iconVariant)};
        }
    }
`;

/**
 * A real photo/illustration (the HR assistant's avatar), not a monochrome glyph — so unlike
 * `StyledSVG` this must NOT force a `fill`. Every other icon mechanism here is a single-colour
 * shape that is meant to pick up the button's variant colour; an avatar has its own colours and
 * forcing one flat fill onto it is what broke it after the icon-button conversion.
 */
const StyledAvatarIcon = styled.img`
    margin: ${({ position }) =>
        position === "right" ? "0 0 0 8px" : position === "left" ? "0 8px 0 0" : position === "center" && "0"};
    cursor: ${({ onClick }) => (onClick ? "pointer" : "inherit")};
    width: 40px;
    height: 40px;
    object-fit: cover;
`;

const buttonSizes = {
    xxsmall: {
        height: "24px",
        padding: "0 8px",
        fontSize: "12px",
        fontWeight: "500",
        letterSpacing: "-0.17px",
    },
    xsmall: {
        height: "32px",
        padding: "0 12px",
        fontSize: "12px",
        fontWeight: "500",
        letterSpacing: "-0.17px",
    },
    small: {
        height: "32px",
        padding: "0 16px",
        fontSize: "14px",
        fontWeight: "500",
        letterSpacing: "-0.17px",
    },

    midSmall: {
        height: "36px",
        padding: "0 16px",
        fontSize: "14px",
        fontWeight: "500",
        letterSpacing: "-0.17px",
    },

    medium: {
        height: "40px",
        padding: "0 20px",
        fontSize: "0.91563rem",
        fontWeight: "500",
        lineHeight: "1.2938rem",
    },
    circle: {
        height: "24px",
        fontSize: "14px",
        fontWeight: "500",
        borderRadius: "50%",
    },
    option: {
        height: "32px",
        padding: "0 8px",
        fontSize: "12px",
        fontWeight: "500",
        letterSpacing: "0",
    },
    /**
     * A cell in a page control. Square at one and two digits, so it matches the icon-only step
     * arrows beside it, and only widens for a three-digit page — a fixed width would clip that.
     *
     * `small` was the closest fit and needed three corrections from the call site to work here;
     * they belong in this table instead, where the next pager gets them for free.
     */
    pager: {
        height: "32px",
        minWidth: "32px",
        padding: "0 8px",
        fontSize: "14px",
        fontWeight: "500",
        letterSpacing: "0",
        // Otherwise the control changes width as 9 becomes 10.
        fontVariantNumeric: "tabular-nums",
        // A digit is not a label, and `borderless` underlines its label on hover.
        underlineOnHover: false,
    },
    toggle: {
        height: "32px",
        padding: "0 24px",
        fontSize: "12px",
        fontWeight: "500",
        letterSpacing: "0",
        borderRadius: "6px",
    },
    unstyled: {
        height: "auto",
        padding: "0",
        fontSize: "12px",
        fontWeight: "500",
    },
};

const baseButtonStyles = (variant) => css`
    height: ${(props) => buttonSizes[props.size]?.height || buttonSizes.medium.height};
    /* Inline-level on purpose, and repeated from Buttonpl -- see the note above it. Being the
       later declaration, this is the one that wins, so the two must stay in step. */
    display: inline-flex;
    justify-content: center;
    border-radius: ${(props) => buttonSizes[props.size]?.borderRadius ?? "8px"};
    /* These two were the only lines here without optional chaining, so an unknown size
       crashed on an icon-only button while every other line fell back to medium. */
    width: ${(props) => (props.icon ? buttonSizes[props.size]?.height || buttonSizes.medium.height : "auto")};
    padding: ${(props) => (props.icon ? "0" : buttonSizes[props.size]?.padding || buttonSizes.medium.padding)};
    font-size: ${(props) => buttonSizes[props.size]?.fontSize || buttonSizes.medium.fontSize} !important;
    font-weight: ${(props) => buttonSizes[props.size]?.fontWeight || buttonSizes.medium.fontWeight};
    letter-spacing: ${(props) => buttonSizes[props.size]?.letterSpacing || "auto"};
    line-height: ${(props) => buttonSizes[props.size]?.lineHeight || "auto"};
    /* Both fall back to the browser default, so a size that does not declare them behaves
       exactly as it did before these lines existed. */
    min-width: ${(props) => buttonSizes[props.size]?.minWidth || "auto"};
    font-variant-numeric: ${(props) => buttonSizes[props.size]?.fontVariantNumeric || "normal"};
`;

const defaultButtonStyles = (variant) => css`
  ${baseButtonStyles}
  border: 1.5px solid ${colors[variant][900]};
  background-color: ${colors[variant][900]};
  color: ${colors[variant][50]};

  &:not(:disabled):hover {
    background-color: ${colors[variant][950]};
    box-shadow: 0 4px 8px ${rgba(colors[variant][950], 0.2)};
  }

  &:not(:disabled):active {
    background-color: ${colors[variant][900]};
    color: ${colors[variant][200]};
    box-shadow: inset 0 2px 4px ${rgba(colors[variant][950], 0.2)};
  }
`;

const invertedButtonStyles = (variant) => css`
  ${baseButtonStyles}
  border: 1.5px solid ${colors[variant][900]};
  
  background-color: ${colors.white};
  color: ${colors[variant][900]};

  &:not(:disabled):hover {
    border: 1.5px solid ${colors[variant][950]};
    box-shadow: 0 4px 8px ${rgba(colors[variant][950], 0.2)};
  }

  &:not(:disabled):active {
    box-shadow: inset 0 2px 4px ${rgba(colors[variant][950], 0.2)};
    color: ${colors[variant][800]};
  }
`;

const borderlessButtonStyles = (variant) => css`
    ${baseButtonStyles}
    border: none;
    background-color: transparent;
    color: ${colors[variant][900]};

    &:not(:disabled):hover {
        /* An icon has no text to underline, and a size can opt out when its content is not a
           label — a page number in a pager, for instance. */
        text-decoration: ${(props) =>
            props.icon || buttonSizes[props.size]?.underlineOnHover === false ? "none" : "underline"};
        color: ${colors[variant][950]};
    }
`;

const optionButtonStyles = (variant) => css`
    ${baseButtonStyles}
    border: 1.5px solid ${colors[variant][900]};
    background-color: transparent;
    color: ${colors[variant][900]};

    &:not(:disabled):hover {
        border: 1.5px solid ${colors[variant][950]};
        box-shadow: 0 4px 8px ${rgba(colors[variant][950], 0.2)};
    }

    &:not(:disabled):active {
        box-shadow: inset 0 2px 4px ${rgba(colors[variant][900], 0.2)};
        color: ${colors[variant][800]};
    }
`;

const selectedOptionButtonStyles = (variant) => css`
    ${baseButtonStyles}
    border: 1.5px solid ${variant === "neutral" ? colors.neutral[700] : colors[variant][900]};
    background-color: ${variant === "neutral" ? colors.neutral[100] : colors[variant][50]};
    color: ${variant === "neutral" ? colors.neutral[700] : colors[variant][900]};

    &:not(:disabled):hover {
        border: 1.5px solid ${colors[variant][950]};
        box-shadow: 0 4px 8px ${rgba(colors[variant][950], 0.2)};
    }

    &:not(:disabled):active {
        box-shadow: inset 0 2px 4px ${rgba(colors[variant][900], 0.2)};
        color: ${colors[variant][800]};
    }
`;

const tagButtonStyles = (variant) => css`
    ${baseButtonStyles}
    border: 1px solid ${colors[variant][400]};
    background-color: ${colors[variant][25]};
    color: ${colors[variant][700]};
`;

const variantStyles = (variant = "primary", type) => {
    switch (type) {
        case "default":
            return defaultButtonStyles(variant);
        case "inverted":
            return invertedButtonStyles(variant);
        case "borderless":
            return borderlessButtonStyles(variant);
        case "option":
            return optionButtonStyles(variant);
        case "selectedOption":
            return selectedOptionButtonStyles(variant);
        case "tag":
            return tagButtonStyles(variant);
        default:
            return undefined;
    }
};

/**
 * A disabled button used to be pixel-identical to an enabled one — measured in the browser:
 * same border colour, same text colour, opacity 1, cursor pointer. Three causes, all fixed:
 * the disabled block below carried no colour and no opacity; every variant's hover rule
 * matched a disabled button too, so it lit up under the pointer while refusing the click
 * (hence the not-disabled guards in the variant styles above); and the inline cursor in
 * ButtonWithText overrode the one disabled rule that did exist.
 *
 * Opacity rather than a per-variant colour table, because that is what every legacy
 * .btn:disabled already resolved to through Bootstrap's base rule. It reproduces the old
 * appearance across all six button styles and every variant without inventing a token.
 *
 * The comment lives out here on purpose: backticks inside a styled-components template
 * literal close the string.
 *
 * `display` is `inline-flex`, not `flex`. Flex is what lays the icon out beside the label, but a
 * bare `display: flex` also makes the button a *block-level* box, while Bootstrap's `.btn` — what
 * every one of these call sites rendered before the conversion — was `inline-block`. So a screen
 * that lines its buttons up through inline flow put each one on its own line instead;
 * `.wizardNavButtons { text-align: right }` in the survey wizard is the reported case, and it
 * broke Back and Save apart with nothing failing to show for it. `inline-flex` restores the outer
 * behaviour and leaves the inner layout untouched — measured, the button's width is unchanged.
 * Inside a flex container the two forms are equivalent anyway, since a flex item is blockified.
 *
 * `vertical-align: middle` is the other half of it, and `.btn` carried that too. An inline-level
 * box aligns on its baseline, and a flex container takes its baseline from its first item — so an
 * icon-then-label button and a label-then-icon button sit on different baselines and drift apart
 * vertically. It measured 2.9px between the survey wizard's own two buttons. Middle alignment has
 * no baseline to disagree about.
 */
const Buttonpl = styled.button`
    border: 0;
    font-size: 16px;
    cursor: pointer;
    outline: none;
    display: inline-flex;
    vertical-align: middle;
    align-items: center;
    cursor: default;

    &:hover {
        cursor: default;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
        box-shadow: none;
    }

    ${({ variant, buttonStyle }) => variantStyles(variant, buttonStyle)}
`;

const ButtonText = styled.span`
    white-space: nowrap;
    ${({ $isMaterial }) =>
        $isMaterial &&
        css`
            display: flex;
            align-items: center;
        `}
`;

const ButtonSpinner = styled(Spinner)`
    margin-left: 8px;
`;

/**
 * The label of a filled button is the light tier, so a spinner beside it needs a track that
 * reads against the fill rather than against white. Bordered and borderless buttons sit on
 * white, where the palette's default track is already right.
 */
const spinnerTrack = (variant, buttonStyle) => {
    const scale = colors[variant] || colors.primary;

    return buttonStyle === "default" || !buttonStyle ? scale[700] : colors.neutral[200];
};

/**
 * Match the spinner to the label's height. The sizes in `buttonSizes` are not all in the
 * same unit — `medium` is `0.91563rem` — so this has to read the unit rather than
 * `parseInt` the string, which would silently yield 0 and render nothing.
 */
const spinnerSize = (size) => {
    const declared = buttonSizes[size]?.fontSize || buttonSizes.medium.fontSize;
    const value = parseFloat(declared);

    if (!value) return 16;

    return Math.round(declared.includes("rem") ? value * 16 : value);
};

const ButtonWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const IconComponent = ({
    iconType,
    icon,
    iconPath,
    avatarPath,
    position,
    testId,
    size = "medium",
    onClick,
    variant,
    iconVariant,
    buttonStyle,
    materialIconStyle,
}) => {
    if (avatarPath) {
        return (
            <StyledAvatarIcon
                data-testid={`avatar-${testId}`}
                src={toAbsoluteUrl(avatarPath)}
                position={position}
                onClick={onClick}
                alt=""
            />
        );
    }

    if (iconType === "material") {
        return (
            <StyledMaterialIcon
                data-testid={`material-${testId}`}
                className={materialIconStyle}
                position={position}
                onClick={onClick}
                variant={variant}
                $iconVariant={iconVariant}
                $buttonStyle={buttonStyle}
                icon={icon}
            />
        );
    }

    /**
     * `iconType="custom"` can only reach `/media/svg/icons/Text/`, because the folder is
     * written into the path below. Most of the app's button icons live in
     * `/media/svg/popleads/` instead, so they had no way to be expressed here at all —
     * which is why those call sites are still on the old Button. `iconPath` takes the whole
     * path and leaves `icon` for the name-based lookups.
     */
    if (iconPath) {
        return (
            <StyledSVG
                data-testid={`path-${testId}`}
                src={toAbsoluteUrl(iconPath)}
                position={position}
                onClick={onClick}
                variant={variant}
                $iconVariant={iconVariant}
                $buttonStyle={buttonStyle}
            />
        );
    }

    if (iconType === "custom") {
        return (
            <StyledSVG
                data-testid={`custom-${testId}`}
                src={toAbsoluteUrl(`/media/svg/icons/Text/${icon}.svg`)}
                position={position}
                onClick={onClick}
                variant={variant}
                $iconVariant={iconVariant}
                $buttonStyle={buttonStyle}
            />
        );
    }

    return (
        <StyledFeatherIcon
            data-testid={`feather-${testId}`}
            icon={icon}
            size="16"
            position={position}
            onClick={onClick}
            variant={variant}
            $iconVariant={iconVariant}
            $buttonStyle={buttonStyle}
        />
    );
};

const ButtonContent = ({
    iconType,
    leftIcon,
    icon,
    rightIcon,
    leftIconPath,
    iconPath,
    avatarPath,
    rightIconPath,
    children,
    loading,
    size,
    rightIconClick,
    leftIconClick,
    variant,
    buttonStyle,
    leftIconVariant,
    rightIconVariant,
    iconVariant,
    materialIconStyle,
}) => (
    <ButtonText $isMaterial={iconType === "material"}>
        {(leftIcon || leftIconPath) && (
            <IconComponent
                onClick={leftIconClick}
                iconType={iconType}
                icon={leftIcon}
                iconPath={leftIconPath}
                position="left"
                testId={leftIcon || leftIconPath}
                size={size}
                variant={variant}
                iconVariant={leftIconVariant || iconVariant}
                buttonStyle={buttonStyle}
                materialIconStyle={materialIconStyle}
            />
        )}
        {!(icon || iconPath || avatarPath) && children}
        {(icon || iconPath || avatarPath) && (
            <IconComponent
                iconType={iconType}
                icon={icon}
                iconPath={iconPath}
                avatarPath={avatarPath}
                position="center"
                testId={icon}
                size={size}
                variant={variant}
                iconVariant={iconVariant}
                buttonStyle={buttonStyle}
                materialIconStyle={materialIconStyle}
            />
        )}
        {(rightIcon || rightIconPath) && (
            <IconComponent
                onClick={rightIconClick}
                iconType={iconType}
                icon={rightIcon}
                iconPath={rightIconPath}
                position="right"
                testId={rightIcon || rightIconPath}
                size={size}
                variant={variant}
                iconVariant={rightIconVariant || iconVariant}
                buttonStyle={buttonStyle}
                materialIconStyle={materialIconStyle}
            />
        )}
        {loading && (
            <ButtonSpinner
                decorative
                size={spinnerSize(size)}
                thickness={2}
                headColor={getIconColor(variant, buttonStyle)}
                trackColor={spinnerTrack(variant, buttonStyle)}
            />
        )}
    </ButtonText>
);

const ButtonWithText = ({
    variant,
    children,
    leftIcon,
    icon,
    rightIcon,
    leftIconPath,
    iconPath,
    avatarPath,
    rightIconPath,
    loading = false,
    tooltip,
    iconType,
    buttonStyle = "default",
    size = "medium",
    rightIconClick,
    leftIconClick,
    onClick,
    iconVariant,
    leftIconVariant,
    rightIconVariant,
    type,
    style,
    materialIconStyle,
    disabled,
    label,
    ...props
}) => {
    // Same guard Switch uses: a button that is waiting on a request must not fire again.
    const effectiveOnClick = loading ? undefined : onClick;
    const effectiveRightIconClick = onClick ? null : rightIconClick;
    const effectiveLeftIconClick = onClick ? null : leftIconClick;

    /**
     * A button whose whole content is an icon has no text for a screen reader to read, so
     * `label` becomes its accessible name. `size="circle"` counts too: its children render
     * as a sibling of the button, not inside it, so the button itself is still nameless.
     *
     * `tooltip` deliberately does not satisfy this — it is a visual hint on hover and never
     * reaches the accessibility tree as a name.
     */
    const hasIcon = Boolean(icon || iconPath || avatarPath);
    const nameless = hasIcon && (!children || size === "circle");

    if (process.env.NODE_ENV !== "production") {
        if (nameless && !label && !props["aria-label"]) {
            // eslint-disable-next-line no-console
            console.warn(
                "Button: an icon-only button needs `label`, which becomes its aria-label. " +
                    "Without it a screen reader announces nothing at all."
            );
        }
        if (onClick && (rightIconClick || leftIconClick)) {
            // eslint-disable-next-line no-console
            console.warn(
                "Button: `rightIconClick`/`leftIconClick` are ignored when `onClick` is set, " +
                    "so the icon handler passed here will never run."
            );
        }
    }

    const content = (
        <ButtonContent
            type={type}
            iconType={iconType}
            leftIcon={leftIcon}
            icon={icon}
            rightIcon={rightIcon}
            leftIconPath={leftIconPath}
            iconPath={iconPath}
            avatarPath={avatarPath}
            rightIconPath={rightIconPath}
            children={size === "circle" ? null : children}
            loading={loading}
            size={size}
            rightIconClick={effectiveRightIconClick}
            leftIconClick={effectiveLeftIconClick}
            onClick={effectiveOnClick}
            variant={variant}
            buttonStyle={buttonStyle}
            iconVariant={iconVariant}
            leftIconVariant={leftIconVariant}
            rightIconVariant={rightIconVariant}
            materialIconStyle={materialIconStyle}
        />
    );

    const button = (
        <Buttonpl
            type={type}
            variant={variant}
            buttonStyle={buttonStyle}
            /**
             * An enabled `<button>` is interactive, so it points. This used to be conditional on
             * `onClick || type === "submit"` and got both halves wrong: it read the `type`
             * *prop*, while a button with no `type` still resolves to `submit` in the DOM, and it
             * could not see a handler attached after render — which is how react-bootstrap's
             * `Dropdown.Toggle` works, so every dropdown trigger in the app showed an arrow.
             * Bootstrap's `.btn` never tried to be clever here either.
             *
             * Written inline rather than in the stylesheet so it wins over the `:disabled` rule
             * there. A caller's own `style` still wins over this, as before.
             */
            style={{ cursor: disabled ? "not-allowed" : "pointer", ...style }}
            icon={icon || iconPath || avatarPath}
            size={size}
            disabled={disabled}
            aria-label={nameless ? label : undefined}
            onClick={effectiveOnClick}
            {...props}
        >
            {tooltip ? (
                <TooltipOverlay placement="bottom" tooltip={tooltip}>
                    <span>{content}</span>
                </TooltipOverlay>
            ) : (
                content
            )}
        </Buttonpl>
    );

    return size === "circle" ? (
        <ButtonWrapper>
            {button}
            {children && <span>{children}</span>}
        </ButtonWrapper>
    ) : (
        button
    );
};

export default ButtonWithText;
